import { useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { Check, ExpandMore, FilterList, Search } from '@mui/icons-material';
import { Form } from 'react-final-form';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import useShopFinderControls from '../hooks/useShopFinderControls';
import {
  DESKTOP_DROPDOWN_HEIGHT,
  FILTER_MENU_ITEMS,
  MAP_OPTIONS,
  POSTCODE,
  SMALL_SCREEN_DROPDOWN_HEIGHT,
} from '../constants';
import { FIELDS } from '../../../constants/forms';
import {
  INVALID_POSTCODE,
  PLEASE_ENTER_VALID_$,
} from '../../../constants/strings';
import usePickupLocations from '../../../hooks/usePickupLocations';
import { StringUtil, UrlUtil } from '../../../utils';
import useBreakpoint from '../../../hooks/useBreakpoint';
import { Validators } from '../../../helpers';
import { FormInput } from '../../FormInput';
import {
  getKindLabel,
  getPickupFilteredJSON,
  isDropdownMenuOverflowing,
} from '../helpers';

const StyledTypography = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(1, 0, 2, 0),
}));

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 3),
  marginTop: theme.spacing(4),
  position: 'relative',
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  border: `1px solid ${theme.vars.palette.text.primary}`,
  color: 'inherit',
}));

const DropdownMenu = styled(Menu)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const CheckIcon = styled(Check)(({ theme }) => ({
  position: 'relative',
  right: theme.spacing(0),
  color: theme.vars.palette.primary.borderLight,
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
}));

const SearchForm = ({
  onSubmit,
  formRef,
  searchAddress,
  initialPostcodeValue,
  urlPostcode,
  handleInputChange,
  isValidPostcode,
}) => (
  <Form
    onSubmit={onSubmit}
    ref={formRef}
    initialValues={{
      postcode: searchAddress || initialPostcodeValue || urlPostcode,
    }}
    render={({ handleSubmit }) => (
      <Box
        component='form'
        autoComplete='off'
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <FormControl fullWidth>
          <FormInput
            id='postcode'
            name={FIELDS.POSTCODE.KEY}
            label={FIELDS.POSTCODE.LABEL}
            placeholder={FIELDS.POSTCODE.PLACEHOLDER}
            variant='standard'
            inputProps={{
              autocomplete: 'off',
            }}
            required
            maxLength={8}
            onChange={e => {
              handleInputChange(e);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.target.blur();
                handleSubmit();
              }
            }}
          />
          <Button
            sx={{
              alignSelf: 'flex-end',
              top: '12px',
              position: 'absolute',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&:focus': {
                backgroundColor: 'transparent',
              },
            }}
            aria-label={FIELDS.POSTCODE.LABEL}
            type='submit'
          >
            <Search aria-label={FIELDS.POSTCODE.LABEL} />
          </Button>

          {!isValidPostcode && (
            <StyledTypography color='error' variant='caption'>
              {`${INVALID_POSTCODE}. ${StringUtil.formatMessage(
                PLEASE_ENTER_VALID_$,
                'UK postcode'
              )}.`}
            </StyledTypography>
          )}
        </FormControl>
      </Box>
    )}
  />
);

const KindFilterDropdown = ({
  allAnchorEl,
  allExpanded,
  handleCloseAll,
  kinds,
  handleAllItemClick,
  selectedAllItem,
  getKindLabel,
}) => {
  const isSmallScreen = useBreakpoint('md');
  const DROPDOWN_ITEM_HEIGHT = useMemo(
    () =>
      isSmallScreen ? SMALL_SCREEN_DROPDOWN_HEIGHT : DESKTOP_DROPDOWN_HEIGHT,
    [isSmallScreen]
  );

  return (
    <DropdownMenu
      anchorEl={allAnchorEl}
      open={allExpanded}
      onClose={handleCloseAll}
      anchorOrigin={{
        vertical: isDropdownMenuOverflowing(
          allAnchorEl,
          DROPDOWN_ITEM_HEIGHT * kinds.length
        )
          ? 'top'
          : 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: isDropdownMenuOverflowing(
          allAnchorEl,
          DROPDOWN_ITEM_HEIGHT * kinds.length
        )
          ? 'bottom'
          : 'top',
        horizontal: 'left',
      }}
      getContentAnchorEl={null}
    >
      <StyledMenuItem
        key='all'
        onClick={() => handleAllItemClick({ key: null, value: 'All' })}
        tabIndex={0}
      >
        <Typography variant='body2'>ALL</Typography>
        {!selectedAllItem && <CheckIcon />}
      </StyledMenuItem>
      {kinds.map(kind => (
        <StyledMenuItem
          key={kind}
          onClick={() =>
            handleAllItemClick({
              key: kind,
              value: getKindLabel(kind),
            })
          }
          tabIndex={0}
        >
          <Typography variant='body2'>{getKindLabel(kind)}</Typography>
          {selectedAllItem?.key === kind && <CheckIcon />}
        </StyledMenuItem>
      ))}
    </DropdownMenu>
  );
};

const AmenitiesFilterDropdown = ({
  filterAnchorEl,
  filterExpanded,
  handleCloseFilter,
  handleFilterItemClick,
  selectedFilterItem,
}) => {
  const isSmallScreen = useBreakpoint('md');
  const DROPDOWN_ITEM_HEIGHT = useMemo(
    () =>
      isSmallScreen ? SMALL_SCREEN_DROPDOWN_HEIGHT : DESKTOP_DROPDOWN_HEIGHT,
    [isSmallScreen]
  );

  return (
    <DropdownMenu
      anchorEl={filterAnchorEl}
      open={filterExpanded}
      onClose={handleCloseFilter}
      anchorOrigin={{
        vertical: isDropdownMenuOverflowing(
          filterAnchorEl,
          DROPDOWN_ITEM_HEIGHT * FILTER_MENU_ITEMS.length
        )
          ? 'top'
          : 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: isDropdownMenuOverflowing(
          filterAnchorEl,
          DROPDOWN_ITEM_HEIGHT * FILTER_MENU_ITEMS.length
        )
          ? 'bottom'
          : 'top',
        horizontal: 'left',
      }}
      getContentAnchorEl={null}
    >
      {FILTER_MENU_ITEMS.map(item => (
        <MenuItem key={item.key} onClick={() => handleFilterItemClick(item)}>
          {item.value}{' '}
          {selectedFilterItem && selectedFilterItem.includes(item.key) && (
            <CheckIcon />
          )}
        </MenuItem>
      ))}
    </DropdownMenu>
  );
};

const Title = ({ isModal, title }) =>
  !isModal &&
  title && (
    <StyledTypography variant='h3' component='h1'>
      {title}
    </StyledTypography>
  );

const ShopFinderSearch = ({
  title,
  data,
  setFilteredLocations,
  onSearch,
  responseError,
  kinds = [],
  setIsValidPostcode,
  setActivePickupPoint,
  isValidPostcode = true,
  isModal = false,
  dropOffFilterParam,
  initialPostcodeValue = '',
  formRef,
  currentLocation,
  searchAddress,
  setSearchAddress,
  pickup,
}) => {
  const urlPostcode = UrlUtil.getURLParam(POSTCODE)?.trim() || '';

  const expandButtonText = () => {
    if (kinds && kinds.length === 1) {
      return getKindLabel(kinds[0]);
    }

    if (selectedAllItem) {
      return selectedAllItem.value;
    }

    return 'All';
  };

  const {
    allAnchorEl,
    allExpanded,
    filterAnchorEl,
    filterExpanded,
    selectedAllItem,
    selectedFilterItem,
    setSelectedFilterItem,
    setSelectedAllItem,
    handleAllClick,
    handleFilterClick,
    handleCloseAll,
    handleCloseFilter,
    handleAllItemClick,
    handleFilterItemClick,
  } = useShopFinderControls(data, setFilteredLocations);

  useEffect(() => {
    applyFilter();
  }, [selectedAllItem, selectedFilterItem, data]);

  const { pickupLocations } = usePickupLocations({
    latitude: currentLocation?.latitude,
    longitude: currentLocation?.longitude,
    ...MAP_OPTIONS,
    dropOff: dropOffFilterParam,
    pickup,
  });

  useEffect(() => {
    if (urlPostcode === '') {
      if (currentLocation) {
        setFilteredLocations(pickupLocations);
      } else if (!isModal) {
        setFilteredLocations([]);
      }
    }
  }, [urlPostcode, setFilteredLocations]);

  const handleInputChange = event => {
    if (!event.target.value && !isModal) {
      UrlUtil.removeURLParam(POSTCODE);
      setSearchAddress(null);
      if (currentLocation) {
        setFilteredLocations(pickupLocations);
      } else {
        setFilteredLocations([]);
      }
    }
    setIsValidPostcode(true);
    setSelectedAllItem(null);
  };

  const performSearch = formValues => {
    const postcode = formValues.postcode.trim();

    if (Validators.isUKPostcodeValid(postcode)) {
      onSearch(postcode);
      setActivePickupPoint(null);
      setSelectedFilterItem([]);
      applyFilter();
      if (!isModal) {
        UrlUtil.updateURLWithParams(POSTCODE, postcode);
      }
    } else {
      setIsValidPostcode(false);
    }
  };

  const handleFormSubmit = useCallback(
    formValues => {
      try {
        performSearch(formValues);
      } catch (error) {
        return error;
      }
    },
    [performSearch]
  );

  const getPickupFilteredByType = (pickupPoint, criteria) =>
    get(pickupPoint, 'pickupLocation.kind') === criteria;

  const filterPickupPoints = (
    pickupPoints,
    filterCriteria = [],
    typeCriteria
  ) =>
    pickupPoints.filter(pickupPoint => {
      const matchesFilter = filterCriteria.length
        ? filterCriteria.every(criteria =>
            getPickupFilteredJSON(pickupPoint, criteria)
          )
        : true;
      const matchesType = typeCriteria
        ? getPickupFilteredByType(pickupPoint, typeCriteria)
        : true;
      return matchesFilter && matchesType;
    });

  const applyFilter = useCallback(() => {
    const filteredData = filterPickupPoints(
      data,
      selectedFilterItem || [],
      selectedAllItem?.key
    );
    setFilteredLocations(filteredData);
  }, [data, selectedFilterItem, selectedAllItem, setFilteredLocations]);

  return (
    <StyledBox id='shop-finder-search'>
      <Title isModal={isModal} title={title} />
      <Box sx={{ mt: 1 }}>
        <SearchForm
          onSubmit={handleFormSubmit}
          formRef={formRef}
          searchAddress={searchAddress}
          initialPostcodeValue={initialPostcodeValue}
          urlPostcode={urlPostcode}
          handleInputChange={handleInputChange}
          isValidPostcode={isValidPostcode}
        />
      </Box>
      {!!data.length && !responseError && isValidPostcode && (
        <>
          <ButtonsContainer>
            <StyledButton
              onClick={handleAllClick}
              variant='outlined'
              color='inherit'
              disabled={kinds && kinds.length <= 1}
            >
              {expandButtonText()}{' '}
              <ExpandMore
                style={{
                  transition: 'transform 0.3s ease',
                  transform: allExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </StyledButton>
            <StyledButton
              onClick={handleFilterClick}
              variant='outlined'
              color='inherit'
            >
              Filters <FilterList />
            </StyledButton>
          </ButtonsContainer>
          {kinds && kinds.length > 1 && (
            <KindFilterDropdown
              allAnchorEl={allAnchorEl}
              allExpanded={allExpanded}
              handleCloseAll={handleCloseAll}
              kinds={kinds}
              handleAllItemClick={handleAllItemClick}
              selectedAllItem={selectedAllItem}
              getKindLabel={getKindLabel}
            />
          )}
          <AmenitiesFilterDropdown
            filterAnchorEl={filterAnchorEl}
            filterExpanded={filterExpanded}
            handleCloseFilter={handleCloseFilter}
            handleFilterItemClick={handleFilterItemClick}
            selectedFilterItem={selectedFilterItem}
          />
        </>
      )}
    </StyledBox>
  );
};

ShopFinderSearch.propTypes = {
  title: PropTypes.string,
  data: PropTypes.array.isRequired,
  setActivePickupPoint: PropTypes.func,
  setFilteredLocations: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  responseError: PropTypes.bool,
  kinds: PropTypes.array,
  setIsValidPostcode: PropTypes.func.isRequired,
  isValidPostcode: PropTypes.bool,
  searchAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.null]),
  setSearchAddress: PropTypes.func,
  dropOffFilterParam: PropTypes.bool,
  pickup: PropTypes.bool,
};

export default ShopFinderSearch;
